import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { NOTIFICATION_TRIGGERS, NotificationRulePreview, mergeFieldsFor } from '@cloudsign/shared';
import type { MergeFieldDef, NotificationFilter, NotificationRule, NotificationRuleCreate } from '@cloudsign/shared';
import { Alert, Badge, Button, Card, CheckboxField, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { EmailListInput, PhoneListInput, RichTextEditor, StatusPill, normaliseRichHtml, splitList } from '../../components/phase2';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { allowedRecipients, emptyRule, rulesKey, useEndpoints, useRules, useSiteFields, useSites, useTriggers } from './api';
import { GroupPicker, SitePicker } from './Pickers';
import { useConnections } from '../integrations/api';
import { useModuleOn } from '../people/api';

const CHANNELS = ['email', 'sms', 'push', 'webhook'] as const;
/** Phase 4 (notifications-phase4 s1): channels whose recipient is one Slack or Teams connection. */
const CONNECTION_CHANNELS = ['slack', 'teams'] as const;
const isConnectionChannel = (c: string): boolean => (CONNECTION_CHANNELS as readonly string[]).includes(c);
/** `connectionId` is added to the recipients by S42; read and written here without widening the shared type. */
type RecipientsP4 = NotificationRuleCreate['recipients'] & { connectionId?: string | null };
const connectionIdOf = (r: NotificationRuleCreate['recipients']): string => (r as RecipientsP4).connectionId ?? '';
const FILTER_KINDS = ['field_equals', 'field_contains', 'field_is_set', 'host_is_set', 'method_in', 'is_member'] as const;
type FilterKind = (typeof FILTER_KINDS)[number];

const BUTTON_LINK = 'inline-flex min-h-10 items-center rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium hover:bg-slate-50 sm:min-h-9';

function toCreate(r: NotificationRule): NotificationRuleCreate {
  const { id: _id, createdAt: _created, updatedAt: _updated, ...rest } = r;
  return { ...rest, recipients: { ...rest.recipients, connectionId: rest.recipients.connectionId ?? null } };
}

export function RuleEditorPage() {
  const { id } = useParams();
  const rules = useRules();
  if (id) {
    if (rules.isError) return <ErrorPanel error={rules.error} onRetry={() => void rules.refetch()} />;
    if (!rules.data) return <Spinner />;
    const found = rules.data.find((r) => r.id === id);
    if (!found) return <Alert>{t('error.not_found')}</Alert>;
    return <RuleForm key={found.id} initial={toCreate(found)} id={found.id} />;
  }
  return <RuleForm initial={emptyRule()} id={null} />;
}

/** Adds a {{merge.field}} to the text. */
function MergeInsert({ fields, onPick, label }: { fields: readonly MergeFieldDef[]; onPick: (path: string) => void; label: string }) {
  return (
    <SelectField label={label} value="" onChange={(e) => e.target.value && onPick(e.target.value)}>
      <option value="">{t('ntf.merge.choose')}</option>
      {fields.map((f) => (
        <option key={f.path} value={f.path}>
          {t(`merge.${f.path}`)}
        </option>
      ))}
    </SelectField>
  );
}

function FilterRow({
  filter,
  onChange,
  onRemove,
  fields,
}: {
  filter: NotificationFilter;
  onChange: (f: NotificationFilter) => void;
  onRemove: () => void;
  fields: { id: string; label: string }[];
}) {
  const fieldSelect = (value: string, set: (v: string) => void) => (
    <SelectField label={t('ntf.filter.field')} value={value} onChange={(e) => set(e.target.value)}>
      <option value="">{t('ntf.filter.chooseField')}</option>
      {fields.map((f) => (
        <option key={f.id} value={f.id}>
          {f.label}
        </option>
      ))}
    </SelectField>
  );
  const yesNo = (value: boolean, set: (v: boolean) => void) => (
    <SelectField label={t('ntf.filter.is')} value={value ? 'yes' : 'no'} onChange={(e) => set(e.target.value === 'yes')}>
      <option value="yes">{t('common.yes')}</option>
      <option value="no">{t('common.no')}</option>
    </SelectField>
  );
  return (
    <li className="rounded-lg border border-slate-200 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <Badge>{t(`ntf.filter.kind.${filter.kind}`)}</Badge>
        <Button variant="ghost" onClick={onRemove}>
          {t('ntf.filter.remove')}
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {filter.kind === 'field_equals' || filter.kind === 'field_contains' ? (
          <>
            {fieldSelect(filter.fieldId, (v) => onChange({ ...filter, fieldId: v }))}
            <TextField label={t('ntf.filter.value')} value={filter.value} maxLength={200} onChange={(e) => onChange({ ...filter, value: e.target.value })} />
          </>
        ) : null}
        {filter.kind === 'field_is_set' ? fieldSelect(filter.fieldId, (v) => onChange({ ...filter, fieldId: v })) : null}
        {filter.kind === 'host_is_set' || filter.kind === 'is_member' ? yesNo(filter.value, (v) => onChange({ ...filter, value: v })) : null}
        {filter.kind === 'method_in' ? (
          <TextField
            label={t('ntf.filter.methods')}
            hint={t('ntf.filter.methodsHint')}
            defaultValue={filter.methods.join(', ')}
            onChange={(e) => onChange({ ...filter, methods: splitList(e.target.value) })}
          />
        ) : null}
      </div>
    </li>
  );
}

function newFilter(kind: FilterKind): NotificationFilter {
  switch (kind) {
    case 'field_equals':
    case 'field_contains':
      return { kind, fieldId: '', value: '' };
    case 'field_is_set':
      return { kind, fieldId: '' };
    case 'host_is_set':
    case 'is_member':
      return { kind, value: true };
    case 'method_in':
      return { kind, methods: [] };
  }
}

function RuleForm({ initial, id }: { initial: NotificationRuleCreate; id: string | null }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<NotificationRuleCreate>(initial);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [newKind, setNewKind] = useState<FilterKind>('field_equals');
  const triggers = useTriggers();
  const endpoints = useEndpoints();
  const sites = useSites();
  const integrationsOn = useModuleOn('integrations');
  const connections = useConnections(integrationsOn);
  const fieldSite = draft.siteIds?.[0] ?? sites.data?.[0]?.id ?? null;
  const siteFields = useSiteFields(fieldSite);
  const set = (patch: Partial<NotificationRuleCreate>) => setDraft((d) => ({ ...d, ...patch }));

  const info = triggers.data?.find((x) => x.key === draft.trigger);
  const def = NOTIFICATION_TRIGGERS.find((x) => x.key === draft.trigger);
  const triggerRecipients: readonly string[] = info?.recipients ?? def?.recipients ?? [];
  const connectionChannel = isConnectionChannel(draft.channel);
  const noRecipients = { host: false, member: false, reception: false, emails: false, phones: false, endpoint: false };
  const allowed = connectionChannel ? noRecipients : allowedRecipients(draft.channel, triggerRecipients);
  const connectionAllowed = triggerRecipients.includes('endpoint');
  const channelChoices: string[] = [...CHANNELS, ...CONNECTION_CHANNELS.filter((c) => (integrationsOn && connectionAllowed) || draft.channel === c)];
  const kindConnections = connectionChannel ? (connections.data ?? []).filter((c) => c.kind === draft.channel && (c.status !== 'disabled' || c.id === connectionIdOf(draft.recipients))) : [];
  const mergeFields = def ? mergeFieldsFor(def.mergeContext).filter((f) => !info || info.mergeFields.includes(f.path)) : [];

  const changeChannel = (channel: NotificationRuleCreate['channel']) => {
    if (isConnectionChannel(channel)) {
      const recipients: RecipientsP4 = { host: false, member: false, reception: false, emails: [], phones: [], endpointId: null, connectionId: null };
      set({ channel, recipients });
      return;
    }
    const a = allowedRecipients(channel, triggerRecipients);
    set({
      channel,
      recipients: {
        host: a.host && draft.recipients.host,
        member: a.member && draft.recipients.member,
        reception: a.reception && draft.recipients.reception,
        emails: a.emails ? draft.recipients.emails : [],
        phones: a.phones ? draft.recipients.phones : [],
        endpointId: a.endpoint ? draft.recipients.endpointId : null,
        connectionId: null,
      },
    });
  };
  const setRecipients = (patch: Partial<RecipientsP4>) => set({ recipients: { ...draft.recipients, ...patch } });

  const validate = (): Record<string, string> => {
    const r = draft.recipients;
    const out: Record<string, string> = {};
    if (!draft.name.trim()) out['name'] = t('ntf.rule.nameRequired');
    if (isConnectionChannel(draft.channel)) {
      if (!connectionIdOf(r)) out['recipients'] = t('p4w27.rule.connectionRequired');
    } else if (draft.channel === 'webhook') {
      if (!r.endpointId) out['recipients'] = t('notify.error.endpoint_required');
    } else if (!(r.host || r.member || r.reception || r.emails.length > 0 || r.phones.length > 0)) out['recipients'] = t('notify.error.no_recipient');
    if (draft.siteIds !== null && draft.siteIds.length === 0) out['siteIds'] = t('ntf.rule.pickSite');
    if (draft.groupIds !== null && draft.groupIds.length === 0) out['groupIds'] = t('ntf.rule.pickGroup');
    return out;
  };

  const save = useMutation({
    mutationFn: () => (id ? api.patch(`/notifications/rules/${id}`, { body: draft }) : api.post('/notifications/rules', { body: draft })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: rulesKey });
      toast(t('common.saved'));
      void nav('/manage/notifications');
    },
  });
  const preview = useMutation({
    mutationFn: () => api.post('/notifications/rules/preview', { body: { rule: draft }, schema: NotificationRulePreview }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found = validate();
    setProblems(found);
    if (Object.keys(found).length === 0) save.mutate();
  };
  const serverErrors = fieldErrors(save.error);
  const err = (k: string) => problems[k] ?? serverErrors[k];

  if (triggers.isError) return <ErrorPanel error={triggers.error} onRetry={() => void triggers.refetch()} />;
  if (!triggers.data) return <Spinner />;
  const fieldChoices = (siteFields.data ?? []).map((f) => ({ id: f.id, label: f.label }));
  const body =
    draft.channel === 'email' ? (
      <RichTextEditor label={t('ntf.rule.body')} value={draft.body} mergeFields={mergeFields} onChange={(html) => set({ body: html })} error={err('body')} hint={t('ntf.rule.bodyEmailHint')} />
    ) : (
      <div className="space-y-2">
        <label htmlFor="ntf-body" className="block text-sm font-medium text-slate-800">
          {t('ntf.rule.body')}
        </label>
        <textarea
          id="ntf-body"
          rows={4}
          maxLength={draft.channel === 'sms' ? 480 : 1000}
          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          value={draft.body}
          onChange={(e) => set({ body: e.target.value })}
        />
        <p className="text-xs text-slate-600">{t('ntf.rule.bodyTextHint')}</p>
        <MergeInsert fields={mergeFields} label={t('ntf.merge.insertBody')} onPick={(p) => set({ body: `${draft.body}{{${p}}}` })} />
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-6" noValidate>
      <PageHeader
        title={id ? t('ntf.rule.editTitle') : t('ntf.rule.newTitle')}
        description={t('ntf.rule.description')}
        actions={
          <Link to="/manage/notifications" className={BUTTON_LINK}>
            {t('common.back')}
          </Link>
        }
      />
      <Card title={t('ntf.rule.when')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t('ntf.rule.name')} value={draft.name} maxLength={100} onChange={(e) => set({ name: e.target.value })} error={err('name')} />
          <SelectField label={t('ntf.rule.trigger')} value={draft.trigger} error={err('trigger')} onChange={(e) => set({ trigger: e.target.value as NotificationRuleCreate['trigger'] })}>
            {triggers.data.map((x) => (
              <option key={x.key} value={x.key}>
                {t(`notify.trigger.${x.key}`)}
              </option>
            ))}
          </SelectField>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <SitePicker value={draft.siteIds} onChange={(siteIds) => set({ siteIds })} error={err('siteIds')} />
          <GroupPicker value={draft.groupIds} onChange={(groupIds) => set({ groupIds })} error={err('groupIds')} />
        </div>
        <div className="mt-4">
          <CheckboxField label={t('ntf.rule.enabled')} checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        </div>
      </Card>

      <Card title={t('ntf.rule.filters')} description={t('ntf.rule.filtersHint')}>
        {draft.filters.length === 0 ? <p className="mb-3 text-sm text-slate-600">{t('ntf.rule.noFilters')}</p> : null}
        <ul className="mb-3 space-y-3">
          {draft.filters.map((f, i) => (
            <FilterRow
              key={i}
              filter={f}
              fields={fieldChoices}
              onChange={(next) => set({ filters: draft.filters.map((x, j) => (j === i ? next : x)) })}
              onRemove={() => set({ filters: draft.filters.filter((_, j) => j !== i) })}
            />
          ))}
        </ul>
        {fieldSite ? <p className="mb-3 text-xs text-slate-600">{t('ntf.filter.fieldsFrom', { site: sites.data?.find((s) => s.id === fieldSite)?.name ?? '' })}</p> : null}
        <div className="flex flex-wrap items-end gap-3">
          <SelectField label={t('ntf.filter.add')} value={newKind} onChange={(e) => setNewKind(e.target.value as FilterKind)}>
            {FILTER_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`ntf.filter.kind.${k}`)}
              </option>
            ))}
          </SelectField>
          <Button disabled={draft.filters.length >= 10} onClick={() => set({ filters: [...draft.filters, newFilter(newKind)] })}>
            {t('ntf.filter.addButton')}
          </Button>
        </div>
      </Card>

      <Card title={t('ntf.rule.send')}>
        <SelectField label={t('ntf.rule.channel')} value={draft.channel} error={err('channel')} className="max-w-xs" onChange={(e) => changeChannel(e.target.value as NotificationRuleCreate['channel'])}>
          {channelChoices.map((c) => (
            <option key={c} value={c}>
              {isConnectionChannel(c) ? t(`p4w27.rule.channel.${c}`) : t(`ntf.channel.${c}`)}
            </option>
          ))}
        </SelectField>
        <fieldset className="mt-4 space-y-3">
          <legend className="mb-1 text-sm font-medium text-slate-800">{t('ntf.rule.recipients')}</legend>
          {allowed.host ? <CheckboxField label={t('ntf.recipient.host')} checked={draft.recipients.host} onChange={(e) => setRecipients({ host: e.target.checked })} /> : null}
          {allowed.member ? <CheckboxField label={t('ntf.recipient.member')} checked={draft.recipients.member} onChange={(e) => setRecipients({ member: e.target.checked })} /> : null}
          {allowed.reception ? (
            <CheckboxField label={t('ntf.recipient.reception')} hint={t('ntf.recipient.receptionHint')} checked={draft.recipients.reception} onChange={(e) => setRecipients({ reception: e.target.checked })} />
          ) : null}
          {allowed.emails ? <EmailListInput label={t('ntf.recipient.emails')} value={draft.recipients.emails} max={20} onChange={(emails) => setRecipients({ emails })} /> : null}
          {allowed.phones ? <PhoneListInput label={t('ntf.recipient.phones')} value={draft.recipients.phones} max={20} onChange={(phones) => setRecipients({ phones })} /> : null}
          {allowed.endpoint ? (
            <SelectField label={t('ntf.recipient.endpoint')} value={draft.recipients.endpointId ?? ''} onChange={(e) => setRecipients({ endpointId: e.target.value || null })} hint={t('ntf.recipient.endpointHint')}>
              <option value="">{t('ntf.recipient.chooseEndpoint')}</option>
              {(endpoints.data ?? [])
                .filter((x) => x.status !== 'paused')
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
            </SelectField>
          ) : null}
          {connectionChannel ? (
            <>
              <SelectField
                label={t('p4w27.rule.connection')}
                hint={t('p4w27.rule.connectionHint')}
                value={connectionIdOf(draft.recipients)}
                onChange={(e) => setRecipients({ connectionId: e.target.value || null })}
              >
                <option value="">{t('p4w27.rule.chooseConnection')}</option>
                {kindConnections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </SelectField>
              {connections.isError ? <p className="text-sm text-slate-700">{t('p4w27.rule.connectionsUnavailable')}</p> : null}
              {connections.data && kindConnections.length === 0 ? (
                <p className="text-sm text-slate-700">
                  {t('p4w27.rule.noConnections')}{' '}
                  <Link to="/manage/integrations" className="text-brand underline">
                    {t('p4w27.rule.manageConnections')}
                  </Link>
                </p>
              ) : null}
            </>
          ) : null}
          {err('recipients') ? <p className="text-sm text-red-700">{err('recipients')}</p> : null}
        </fieldset>
      </Card>

      {draft.channel !== 'webhook' ? (
        <Card title={t('ntf.rule.message')} description={t('ntf.rule.messageHint')}>
          <div className="space-y-4">
            {draft.channel !== 'sms' ? (
              <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
                <TextField label={t('ntf.rule.subject')} value={draft.subject} maxLength={200} onChange={(e) => set({ subject: e.target.value })} error={err('subject')} />
                <MergeInsert fields={mergeFields} label={t('ntf.merge.insertSubject')} onPick={(p) => set({ subject: `${draft.subject}{{${p}}}` })} />
              </div>
            ) : null}
            {body}
          </div>
        </Card>
      ) : (
        <Alert kind="info">{t('ntf.rule.webhookNote')}</Alert>
      )}

      <Card
        title={t('ntf.preview.title')}
        footer={
          <Button disabled={preview.isPending} onClick={() => preview.mutate()}>
            {t('ntf.preview.button')}
          </Button>
        }
      >
        <ErrorLine error={preview.error} />
        {preview.data ? <PreviewPane preview={preview.data} /> : <p className="text-sm text-slate-600">{t('ntf.preview.empty')}</p>}
      </Card>

      <ErrorLine error={save.error} />
      <div className="flex justify-end gap-2">
        <Link to="/manage/notifications" className={BUTTON_LINK}>
          {t('common.cancel')}
        </Link>
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

function PreviewPane({ preview }: { preview: NotificationRulePreview }) {
  return (
    <div className="space-y-3 text-sm" aria-live="polite">
      {preview.warnings.map((w) => (
        <Alert key={w} kind="info">
          {w}
        </Alert>
      ))}
      <p>
        <span className="font-medium">{t('ntf.preview.subject')}: </span>
        {preview.subject}
      </p>
      {preview.channel === 'email' ? (
        // Safe: the server sanitises the body and normaliseRichHtml rebuilds it from the allowed subset.
        <div
          className="rounded-md border border-slate-200 p-3 [&_a]:text-brand [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: normaliseRichHtml(preview.body) }}
        />
      ) : (
        <p className="whitespace-pre-wrap rounded-md border border-slate-200 p-3">{preview.body}</p>
      )}
      <ul className="flex flex-wrap gap-2">
        {preview.recipients.map((r) => (
          <li key={r.kind}>
            <StatusPill tone="neutral">{t('ntf.preview.recipient', { kind: t(`ntf.recipientKind.${r.kind}`), count: r.count })}</StatusPill>
          </li>
        ))}
      </ul>
    </div>
  );
}
